/**
 * Keeps `--vvh` and `--vv-offset-top` CSS variables in sync with the *visual*
 * viewport.
 *
 * On mobile the on-screen keyboard shrinks the visual viewport but NOT the
 * layout viewport, so `100dvh` (the dynamic viewport unit) stays at full
 * height when the keyboard opens. A fixed-height app shell sized with `100dvh`
 * therefore overflows behind the keyboard, and the browser scrolls the focused
 * input into view — pushing a sticky header off the top of the screen.
 *
 * `window.visualViewport.height` does track the keyboard, so we mirror it into
 * `--vvh` and size the shell with `height: var(--vvh, 100dvh)`. The `100dvh`
 * fallback covers browsers without the VisualViewport API (and SSR/first paint).
 *
 * On iOS (Safari and WKWebView alike) shrinking the shell is not enough: when
 * an input near the bottom is focused, the engine *also* pans the page up to
 * reveal the caret — computed against the pre-shrink layout — and that pan is
 * applied to the visual viewport itself (`visualViewport.offsetTop > 0`), not
 * as a layout scroll, so the CSS overflow locks on html/body can't prevent it.
 * The visible window becomes [offsetTop, offsetTop + vvh] while the shell
 * still occupies [0, vvh], leaving a white band as tall as the pan between a
 * bottom-anchored composer and the keyboard. We mirror the offset into
 * `--vv-offset-top` and the body translates itself down by it (see index.css),
 * keeping the shell glued to the visible region. Pinch-zoom pans also move
 * `offsetTop`, so the offset is only applied at scale 1 — the keyboard pan
 * always happens unzoomed.
 *
 * @returns a cleanup function that removes the listeners.
 */
export function initViewportHeight(): () => void {
  if (typeof window === 'undefined') return () => {}

  const vv = window.visualViewport

  const update = () => {
    const height = vv?.height ?? window.innerHeight
    document.documentElement.style.setProperty('--vvh', `${height}px`)

    const offsetTop = vv && vv.scale === 1 ? vv.offsetTop : 0
    document.documentElement.style.setProperty('--vv-offset-top', `${offsetTop}px`)

    // The document is deliberately non-scrollable (html/body overflow locks),
    // but a root with `overflow: hidden` is still *programmatically*
    // scrollable, and iOS focus handling sometimes pans via a layout scroll
    // instead of (or on top of) the visual-viewport offset. Any layout scroll
    // here is therefore a focus-pan artifact — undo it.
    if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0)
  }

  update()

  vv?.addEventListener('resize', update)
  vv?.addEventListener('scroll', update)
  window.addEventListener('resize', update)

  return () => {
    vv?.removeEventListener('resize', update)
    vv?.removeEventListener('scroll', update)
    window.removeEventListener('resize', update)
  }
}
